import { Stack, Tooltip, Typography } from '@mui/material'
import { DataGridPro, GridColDef, GridRowParams, useGridApiRef } from '@mui/x-data-grid-pro'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Maybe, Mutation_Root, ReportInput } from 'src/@types/graphql'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { ReportEvaluationNeedDetails } from 'src/screens/reporting/shared/report-card-content/report-evaluation-need/details/ReportEvaluationNeedDetails'
import { EVALUATION_NEEDS, PROJECT, PROJECT_TYPE } from 'src/shared/constants/constants'
import { DataGridContainer } from 'src/shared/grid/DataGridContainer'
import { S } from 'src/shared/styled/S'
import { useGridTranslateHook } from 'src/shared/utils/hooks/grid-translate-hook'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { muiTheme } from 'src/theme/theme'
import styled from 'styled-components'
import { gql, useClient } from 'urql'

const DataGridProStyled = styled(DataGridPro)`
  & .MuiDataGrid-row {
    &:hover {
      cursor: pointer;
      background: ${({ theme }) => theme.colors.secondary.lightHover};
    }
  }
`

const fetchEvaluationNeedReportDataMutation = gql`
  mutation fetchEvaluationNeedReportData($report: ReportInput!, $isPfKap: Boolean!) {
    fetchPfKapProjectEvaluationNeedReportData(report: $report) @include(if: $isPfKap) {
      data {
        type
        count
      }
    }
    fetchPfPgvProjectEvaluationNeedReportData(report: $report) @skip(if: $isPfKap) {
      data {
        type
        count
      }
    }
  }
`

interface Props {
  reportInput: ReportInput
  process: PROJECT_TYPE
}

interface EvaluationResponse {
  type: Maybe<string>
  count: number
}

export const ReportEvaluationNeedGrid = ({ reportInput, process }: Props) => {
  const { getMessage } = useMessageSource()

  const apiRef = useGridApiRef()
  const gridTranslations = useGridTranslateHook()
  const urqlClient = useClient()
  const notificationService = useNotificationService()

  const [evaluationNeedReport, setEvaluationNeedReport] = useState<EvaluationResponse[]>([])
  const [selectedEvaluationNeed, setSelectedEvaluationNeed] = useState<string>()
  const [modalOpen, setModalOpen] = useState<boolean>(false)

  const fetchData = useCallback(async () => {
    const isPfKap = process === PROJECT.PF_KAP

    const { data } = await urqlClient
      .mutation<{
        fetchPfKapProjectEvaluationNeedReportData: Mutation_Root['fetchPfKapProjectEvaluationNeedReportData']
        fetchPfPgvProjectEvaluationNeedReportData: Mutation_Root['fetchPfPgvProjectEvaluationNeedReportData']
      }>(fetchEvaluationNeedReportDataMutation, {
        report: reportInput,
        isPfKap: isPfKap,
      })
      .toPromise()

    if (data) {
      const actualData = isPfKap
        ? data.fetchPfKapProjectEvaluationNeedReportData.data
        : data.fetchPfPgvProjectEvaluationNeedReportData.data

      const mappedData: EvaluationResponse[] = Object.keys(EVALUATION_NEEDS[process]).map((it) => ({
        type: it,
        count: actualData.filter((responseItem) => responseItem.type === it)[0]?.count ?? 0,
      }))

      mappedData.push({
        type: null,
        count: actualData.filter((item) => item.type === null)?.[0]?.count ?? 0,
      })

      setEvaluationNeedReport(mappedData)
    } else {
      notificationService.operationFailed()
    }
  }, [urqlClient, reportInput, process, notificationService])

  const rows = useMemo(() => evaluationNeedReport.map((type, i) => ({ ...type, id: i })), [evaluationNeedReport])

  const columns = useMemo(
    () =>
      [
        {
          field: 'type',
          flex: 1,
          sortable: false,
          renderCell({ value }) {
            return (
              <S.DataGrid.Typography variant="subtitle2" sx={{ color: muiTheme.palette.text.disabled }}>
                {value === null ? getMessage('label.not.available') : getMessage(`label.evaluation.need.${value}`)}
              </S.DataGrid.Typography>
            )
          },
        },
        {
          field: 'count',
          flex: 1,
          sortable: false,
          type: 'number',
        },
      ] as GridColDef[],
    [getMessage],
  )

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleRowClick = ({ row }: GridRowParams) => {
    const { type } = row
    setSelectedEvaluationNeed(type)
    setModalOpen(true)
  }

  return (
    <>
      <DataGridContainer>
        <DataGridProStyled
          apiRef={apiRef as any}
          rows={rows}
          columns={columns}
          localeText={gridTranslations}
          disableColumnResize
          disableColumnSelector
          disableColumnReorder
          disableColumnPinning
          disableRowSelectionOnClick
          disableColumnMenu
          disableColumnFilter
          disableMultipleColumnsSorting
          hideFooter
          columnHeaderHeight={0}
          onRowClick={handleRowClick}
          slots={{
            columnHeaders: () => null,
            toolbar: () => (
              <S.DataGrid.ToolbarContainer $reporting $noActionButton>
                <Stack direction="row" alignItems="left" mb={1}>
                  <Typography>{getMessage('label.evaluation.needs')}</Typography>
                  <Tooltip title={getMessage('tooltip.reporting.evaluation')}>
                    <S.Icons.GridTooltipInfo />
                  </Tooltip>
                </Stack>
              </S.DataGrid.ToolbarContainer>
            ),
          }}
        />
      </DataGridContainer>
      <ReportEvaluationNeedDetails
        reportInput={reportInput}
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
        evaluationNeed={selectedEvaluationNeed}
        process={process}
      />
    </>
  )
}
